"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PORTAL_LAUNCH_NEXT = exports.PORTAL_VAULT_REFUSALS = exports.portalVaultRevealSchema = exports.PORTAL_VAULT_FIELDS = exports.portalVaultUnlockSchema = exports.PORTAL_VAULT_IDLE_LOCK_SECONDS = exports.PORTAL_VAULT_REVEAL_SECONDS = exports.PORTAL_VAULT_TOKEN_TTL_SECONDS = exports.PORTAL_VAULT_TOKEN_HEADER = exports.PAIRING_REFUSALS = exports.pairingClaimSchema = exports.PAIRING_CODE_LENGTH = exports.PAIRING_CODE_ALPHABET = exports.PORTAL_MIN_REMAINING_BALANCE_IQD = exports.PORTAL_ORDER_REFUSALS = exports.PORTAL_ORDER_PAYMENT_FALLBACK = exports.portalShopOrderSchema = exports.PORTAL_ORDER_PAYMENTS = exports.deviceGoodbyeSchema = exports.PORTAL_SESSION_END_REASONS = exports.PORTAL_LAUNCH_PROBLEM_CODES = exports.PORTAL_TIMER_MODES = exports.PORTAL_LOGIN_REFUSALS = exports.PORTAL_REFILL_MESSAGE = exports.PORTAL_REGISTER_REFUSALS = exports.portalRegisterSchema = exports.googleQrPollSchema = exports.portalCardLoginSchema = exports.portalPasswordLoginSchema = exports.PORTAL_LOGIN_METHODS = exports.portalDeviceTokenSchema = exports.PORTAL_DEVICE_TOKEN_HEADER = void 0;
exports.normalizePairingCode = normalizePairingCode;
const zod_1 = require("zod");
const customers_1 = require("./customers");
/**
 * The customer-facing PC portal — SPEC §9, login screen §9.1.
 *
 * This is the half of the product the customer touches: the launcher shown on a
 * locked gaming PC. Everything here crosses a **trust boundary the staff app
 * does not have**. The staff app runs on machines behind the counter operated
 * by employees; this runs on the machines customers sit at, unattended, all
 * evening. Two consequences shape every contract below:
 *
 * 1. **The PC is not trusted to say who it is.** A device proves its identity
 *    with {@link PORTAL_DEVICE_TOKEN_HEADER}, never with a plain device id in
 *    the body — otherwise any customer could start a session on someone else's
 *    machine, or on a machine that is out of service.
 * 2. **The PC is not trusted to say who the customer is.** It sends credentials
 *    or a card id and the server decides. The portal never receives a staff
 *    token and can never reach a staff endpoint.
 */
/* -------------------------------------------------------------------------- */
/* Device identity                                                            */
/* -------------------------------------------------------------------------- */
/**
 * Header carrying the device's provisioning token.
 *
 * The token is a stateless HMAC over the venue's `ENCRYPTION_MASTER_KEY`
 * binding the device id — there is no token table in the schema yet, and the
 * realtime gateway will use the same scheme so the two converge rather than
 * needing reconciliation later.
 *
 * **This means there is no per-device revocation.** Rotating a single PC's
 * token is not possible without rotating the venue master key, which would
 * invalidate every device at once. Accepted deliberately for now; a token table
 * is the fix when the schema can take one.
 */
exports.PORTAL_DEVICE_TOKEN_HEADER = 'x-device-token';
/** `<deviceId>.<base64url signature>` — opaque to the client, which only stores and echoes it. */
exports.portalDeviceTokenSchema = zod_1.z
    .string()
    .min(3)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, 'That device token is malformed.');
/* -------------------------------------------------------------------------- */
/* Sign-in                                                                    */
/* -------------------------------------------------------------------------- */
/**
 * The three routes of SPEC §9.1, side by side on the lock screen.
 *
 * `GOOGLE_QR` is Google's **device flow** ("TV & limited input"), chosen because
 * a LAN kiosk cannot receive a web-OAuth redirect: the PC shows a QR of
 * Google's verification URL plus a short code, the customer finishes on their
 * phone, and the server polls Google for the outcome. The venue enables it by
 * supplying an OAuth client (env), surfaced via
 * {@link PortalCapabilities.googleQr}.
 */
exports.PORTAL_LOGIN_METHODS = ['PASSWORD', 'CARD', 'GOOGLE_QR'];
/**
 * Email OR username, whichever the customer was given. Staff can set either,
 * and a customer who was told "your username is zhewar" should not have to
 * discover that the box wants an email.
 */
exports.portalPasswordLoginSchema = zod_1.z.object({
    identifier: zod_1.z.string().trim().min(1, 'Enter your email or username.').max(160),
    password: zod_1.z.string().min(1, 'Enter your password.').max(200),
});
/**
 * A card tap at the PC. Gated on `VenueSettings.enableCardLogin`; the read is
 * held for `cardLoginHoldSeconds` on the client so a card brushed past a reader
 * in someone's wallet does not sign them in.
 */
exports.portalCardLoginSchema = zod_1.z.object({
    cardId: zod_1.z.string().trim().min(1).max(64),
});
exports.googleQrPollSchema = zod_1.z.object({
    handle: zod_1.z.string().trim().min(1).max(128),
});
/* -------------------------------------------------------------------------- */
/* Self-registration                                                          */
/* -------------------------------------------------------------------------- */
/**
 * Creating your own account at the locked PC (SPEC §9 —
 * `VenueSettings.enablePortalAccountCreation`, surfaced as
 * {@link PortalCapabilities.accountCreation}).
 *
 * DERIVED from the staff `createCustomerSchema`, never duplicated, so the two
 * cannot drift on what a valid username or password is. The differences are
 * deliberate: `username` and `password` are REQUIRED here (a portal account
 * with no way to sign in is useless), and the staff-only fields — phone,
 * cardId, discountPercent, note — are absent, because a kiosk must not be able
 * to grant itself a discount or claim someone's card. Email stays optional; an
 * account that has one can also use the Google QR sign-in.
 */
exports.portalRegisterSchema = customers_1.createCustomerSchema
    .pick({ fullName: true, email: true, username: true, password: true })
    .required({ username: true, password: true });
/**
 * Deliberately SEPARATE from {@link PORTAL_LOGIN_REFUSALS}: membership of the
 * login list is what feeds the kiosk's full-screen-block hoisting, and a
 * failed registration must render inline in its own dialog, never take over
 * the screen. Same precedent as {@link PAIRING_REFUSALS}.
 */
exports.PORTAL_REGISTER_REFUSALS = [
    /** 409 — the venue switched self-registration off; re-read server-side. */
    'account_creation_disabled',
    /** 409 — `CustomersService.assertUsernameAvailable` already speaks this. */
    'username_taken',
    /**
     * 409 — an ACTIVE member already uses this email (wave-3 review R3#5:
     * without the check, a kiosk walk-in could squat a member's address and
     * break their email sign-in).
     */
    'email_taken',
    /** 429 — per-device fixed-window throttle, same code as pairing/hotkey. */
    'too_many_attempts',
];
/* -------------------------------------------------------------------------- */
/* The balance gate                                                           */
/* -------------------------------------------------------------------------- */
/**
 * The owner's exact wording, shown when an identified customer has nothing to
 * spend (SPEC §9.1).
 *
 * Kept as a constant so a test can assert it and so the English never drifts;
 * the portal renders the `portal.noBalance` translation, with this as the
 * English source of truth. Do not reword it — staff will be answering "what
 * does this say" at the counter, and the wording is what the owner asked for.
 */
exports.PORTAL_REFILL_MESSAGE = 'No money in account please refill';
/**
 * Why a sign-in did not produce a session.
 *
 * `no_balance` is deliberately NOT an authentication failure: the customer
 * proved who they are, and telling them "wrong password" when their account is
 * simply empty sends them to the counter with the wrong question.
 */
exports.PORTAL_LOGIN_REFUSALS = [
    'invalid_credentials',
    'no_balance',
    'outstanding_bill',
    'account_inactive',
    'card_login_disabled',
    'card_not_recognised',
    'device_busy',
    'device_out_of_service',
    'google_qr_unavailable',
    'google_unreachable',
    /**
     * The venue's EclipseGG service is suspended (EclipseGG platform,
     * 2026-10-19). 403. Every sign-in door refuses it before writing anything,
     * and so does every new sitting; a sitting already running finishes.
     */
    'venue_suspended',
];
/**
 * Which way the session clock runs, and why.
 *
 * Not inferred by the client from a null `secondsRemaining`: the two cases mean
 * opposite things to the person sitting there, and a screen that guesses will
 * eventually guess wrong.
 *
 * - `COUNTDOWN` — a registered customer playing against their own balance. They
 *   have already paid; what they need is how much time the money buys.
 * - `COUNT_UP` — nothing to count down from. Either a staff-reserved walk-in
 *   with no account (they settle at the counter), a `ROUND`-billed table where
 *   time is not what is charged, or a device priced at zero.
 */
exports.PORTAL_TIMER_MODES = ['COUNTDOWN', 'COUNT_UP'];
/**
 * Why the game the customer pressed did not start, in the two cases where
 * Steam is the reason (Steam account switch, 2026-10-14).
 *
 * - `steam_wrong_account` — Steam came back on another account, so no game was
 *   started. The customer can pick the right one in Steam's Switch account
 *   list, and the game then starts by itself.
 * - `steam_login_required` — the account has no saved sign-in on this PC.
 *   Only staff can fix that, by signing it in once with Remember me.
 *
 * The codes are the prefixes the PC agent writes on its launch error
 * (`steam_wrong_account: …`, `steam_login_required: …`), which the staff board
 * already shows.
 */
exports.PORTAL_LAUNCH_PROBLEM_CODES = ['steam_wrong_account', 'steam_login_required'];
/* -------------------------------------------------------------------------- */
/* Finishing                                                                  */
/* -------------------------------------------------------------------------- */
/**
 * What ended the session — the customer, their credit running out, the clock
 * reaching the end of what was booked, or the PC being turned off.
 *
 * All four close through the same path, so the bill, the payment and points
 * land exactly as a staff-closed session would. This only says which screen to
 * show.
 *
 * `TIME_UP` is the one the customer did not cause and cannot pre-empt, so its
 * screen has to be the clearest of the first three: a walk-in who paid cash
 * for an hour has no balance to top up and no sign-out they forgot to press,
 * and "out of credit" would send them to the counter arguing about money that
 * was never the problem.
 *
 * `POWERED_OFF` (owner, 2026-10-14) is the PC going away under the sitting:
 * Windows shut down or restarted, or the PC was silent for 10 minutes. The
 * server stopped it, billed up to that moment. A PC that is off shows nothing,
 * so the kiosk rarely renders it — but a page still up when the stop lands
 * needs words for it, and every list of reasons must carry it.
 */
exports.PORTAL_SESSION_END_REASONS = [
    'SIGNED_OUT',
    'OUT_OF_CREDIT',
    'TIME_UP',
    'POWERED_OFF',
];
/* -------------------------------------------------------------------------- */
/* The PC is turned off (owner, 2026-10-14)                                   */
/* -------------------------------------------------------------------------- */
/**
 * `POST /portal/device/goodbye` — PortalApp telling the server that Windows is
 * shutting down, restarting or signing out under it (`WM_QUERYENDSESSION`).
 * The owner: "whenever a PC is turned off it should stop their session
 * immediately".
 *
 * Authenticated by the device token ALONE ({@link PORTAL_DEVICE_TOKEN_HEADER}):
 * the machine is going away whoever sits at it, and a staff-reserved walk-in
 * has no portal bearer to send. The server stops THIS device's ACTIVE or
 * PAUSED sitting, billed up to that moment, as the built-in Portal actor.
 *
 * `reason` is only ever `shutdown`: Windows does not tell an application
 * whether it is a restart or a power-off, and both end the sitting the same
 * way. NOT strict, so a later PortalApp may add a field without this server
 * refusing its goodbye — a refused goodbye is a sitting left running on a
 * dark PC, the very bug this exists to fix.
 */
exports.deviceGoodbyeSchema = zod_1.z.object({ reason: zod_1.z.enum(['shutdown']) });
/**
 * Place an order from the PC (SPEC §9).
 *
 * NO DESTINATION AND NO PRICES. The destination is the machine the verified
 * device token names and the session's own tab — a body that could name a tab
 * would let one PC order onto another's bill. Prices come from the product
 * rows at order time, exactly as at the counter.
 */
/**
 * How THIS order is paid (`PortalShopOrderInput.payment`).
 *
 *  - `BALANCE` — take it off the account balance now, at placement, service
 *    charge included when the venue charges one on items.
 *  - `COUNTER` — leave the bill OPEN and settle it at the counter later,
 *    together with the sitting; the till charges (or waives) the service
 *    charge there.
 *
 * Since 2026-09-24 the VENUE decides which one an order may be (§6.1 "Portal
 * Payment Methods", {@link PORTAL_PAYMENT_METHODS}; the API's
 * `portalOrderPayment` is the rule): an account pays from its balance, or at
 * the counter when the venue is `PAY_AT_CASHIER`; a walk-in has no balance,
 * so `COUNTER` is all that can happen to them, and a venue on
 * `ACCOUNT_BALANCE` takes no walk-in order at the PC at all. An explicit
 * `payment` the venue forbids is refused (`portal_payment_not_allowed`); an
 * absent one gets the venue's own answer.
 */
exports.PORTAL_ORDER_PAYMENTS = ['BALANCE', 'COUNTER'];
exports.portalShopOrderSchema = zod_1.z.object({
    items: zod_1.z
        .array(zod_1.z.object({
        productId: zod_1.z.string().trim().min(1).max(64),
        quantity: zod_1.z.number().int().min(1).max(20),
        note: zod_1.z.string().trim().max(200).optional().nullable(),
    }))
        .min(1, 'Add at least one item.')
        .max(20),
    /**
     * Optional on the wire on purpose. A kiosk running a build from before the
     * choice existed sends no field, and must keep behaving exactly as it did —
     * see `PORTAL_ORDER_PAYMENT_FALLBACK`. Absent is not "unknown, ask someone";
     * it is a version, and the version's answer is known.
     */
    payment: zod_1.z.enum(exports.PORTAL_ORDER_PAYMENTS).optional(),
    /**
     * The kiosk's own name for THIS submission of THIS cart, sent unchanged on
     * every retry of it. A key the sitting has already been answered on gets
     * that same order back instead of a second one.
     *
     * Exists because the answer to this POST can be lost AFTER the server has
     * acted on it — the venue uplink resets between the ticket being written
     * and the response being read — and the customer's only visible next step
     * was to press Place order again, which put a second ticket in the kitchen
     * and a second debit on the balance (review find, 2026-09-08). The kiosk
     * mints a fresh key the moment the cart changes; the same cart keeps its
     * key. Optional on the wire for the same reason `payment` is: a kiosk built
     * before the key existed sends none and is placed every time, as it always
     * was.
     */
    orderKey: zod_1.z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9_-]{8,64}$/, 'That order key is malformed.')
        .optional(),
    /**
     * The whole percent the cart quoted as its service-charge line — the venue's
     * rate when its scope reaches ITEMS, else 0 — so the balance is never
     * charged a rate the customer was not shown. A pay-now order whose quote no
     * longer matches the venue's rate is refused (`service_charge_changed`)
     * before anything is written, and the kiosk reloads the menu and quotes
     * again (review find, 2026-09-24: the owner switching the charge on while
     * the Shop was open took 10% nobody saw). Ignored for an order left for the
     * counter, which moves no money at the PC. Optional for the reason `payment`
     * is: a kiosk built before it sends none and is placed as before.
     */
    quotedServiceChargePercent: zod_1.z.number().int().min(0).max(100).optional(),
});
/**
 * What an order with no `payment` field means. Pinned here rather than
 * defaulted inside the zod schema so the back-compatibility promise is
 * something both sides can read and a test can name.
 */
exports.PORTAL_ORDER_PAYMENT_FALLBACK = 'BALANCE';
/** Refusal codes the shop screen must discriminate on. */
exports.PORTAL_ORDER_REFUSALS = [
    /** The venue has portal ordering switched off (VenueSettings). */
    'portal_orders_disabled',
    'product_not_found',
    /** Ordering needs a running session — the kitchen must know which seat. */
    'session_not_running',
    /**
     * Paying from the balance would leave less than the play reserve behind
     * ({@link PORTAL_MIN_REMAINING_BALANCE_IQD}). Discriminated because the
     * customer has a way out the generic copy cannot offer: order the same
     * thing again and pay at the counter.
     */
    'min_balance_required',
    /**
     * The venue's "Portal Payment Methods" forbids how this order asked to be
     * paid (`portalOrderPayment`): paying from the balance at a
     * `PAY_AT_CASHIER` venue, the counter at any other, or any order at all
     * from a walk-in at an `ACCOUNT_BALANCE` venue. Nothing was written. 409.
     */
    'portal_payment_not_allowed',
    /**
     * A pay-now order's `quotedServiceChargePercent` is not the venue's items
     * rate any more — the owner changed it while the Shop was open. Nothing was
     * written; reload the menu, show the new total, and let the customer place
     * it again. 409.
     */
    'service_charge_changed',
];
/**
 * The floor a portal purchase must leave in the account (owner rule): after
 * buying, at least this much balance must REMAIN, so the shop can never eat
 * the money the sitting itself is running on. A purchase that would leave
 * less is refused outright — the order is never created, no ticket prints.
 * Sign-out settlement is deliberately NOT bound by this: play drains the
 * reserve to zero, which is what the reserve is for.
 */
exports.PORTAL_MIN_REMAINING_BALANCE_IQD = 5_000;
/* -------------------------------------------------------------------------- */
/* Pairing codes (WAVE2-DESIGN §3)                                            */
/* -------------------------------------------------------------------------- */
/**
 * The alphabet pairing codes are minted from: no 0/O, 1/I/L — a staff member
 * reads the code across a room and a customer-facing keyboard types it.
 */
exports.PAIRING_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
exports.PAIRING_CODE_LENGTH = 8;
/** Normalise what a human typed: case, separators, stray spaces. */
function normalizePairingCode(raw) {
    return raw.toUpperCase().replace(/[^0-9A-Z]/g, '');
}
/** POST /portal/pair — unauthenticated; the code is the whole credential. */
exports.pairingClaimSchema = zod_1.z.object({
    code: zod_1.z
        .string()
        .trim()
        .min(exports.PAIRING_CODE_LENGTH)
        .max(exports.PAIRING_CODE_LENGTH + 3)
        .transform(normalizePairingCode)
        .refine((value) => value.length === exports.PAIRING_CODE_LENGTH, 'That code is not valid.'),
});
/**
 * One generic refusal for wrong, expired and already-claimed alike —
 * enumeration hygiene, the same stance as device-token verification. Plus the
 * throttle's own code.
 */
exports.PAIRING_REFUSALS = ['pairing_code_invalid', 'too_many_attempts'];
/* -------------------------------------------------------------------------- */
/* The accounts vault (owner, 2026-09-08)                                     */
/* -------------------------------------------------------------------------- */
/**
 * An "Accounts" app inside the kiosk's Apps tab: every venue launcher account
 * — Steam, Epic, Riot — with its username, password, login email and that
 * mailbox's password, filterable by game, on ANY PC.
 *
 * Opening it takes the venue's vault password. A correct one earns a short
 * token bound to this PC and this sitting; the listing below carries no
 * secret at all, and each secret leaves the server one field at a time,
 * through an audited reveal. Editing is the owner's alone, in the staff app.
 */
exports.PORTAL_VAULT_TOKEN_HEADER = 'x-vault-token';
/** How long an unlock lasts. The page locks itself sooner when idle. */
exports.PORTAL_VAULT_TOKEN_TTL_SECONDS = 600;
/** How long a revealed secret stays readable before the page masks it again. */
exports.PORTAL_VAULT_REVEAL_SECONDS = 30;
/** The page locks itself after this long without a press. */
exports.PORTAL_VAULT_IDLE_LOCK_SECONDS = 120;
exports.portalVaultUnlockSchema = zod_1.z.object({
    password: zod_1.z.string().min(1).max(200),
});
exports.PORTAL_VAULT_FIELDS = ['password', 'emailPassword'];
exports.portalVaultRevealSchema = zod_1.z.object({
    field: zod_1.z.enum(exports.PORTAL_VAULT_FIELDS),
});
/**
 * Why the vault did not open. `vault_locked` carries `retryAfterSeconds` in
 * the refusal body; the page shows the countdown rather than a retry button.
 */
exports.PORTAL_VAULT_REFUSALS = [
    'vault_not_set',
    'vault_locked',
    'vault_wrong_password',
    'vault_token_invalid',
];
/* -------------------------------------------------------------------------- */
/* Pressing Play: what happens next (owner, 2026-10-05)                       */
/* -------------------------------------------------------------------------- */
/**
 * What the kiosk does once `POST /portal/launch` has answered:
 *
 * - `play`: the game or app is starting, so the screen shows the usual
 *   "Starting…" overlay.
 * - `sign_in_steam`: the customer chose "Login using my account" for a Steam
 *   game, the first time this sitting. Steam was restarted on its sign-in
 *   screen and nothing was started. The customer signs in (Remember me
 *   unticked), then presses Play now again. Staff are told to sign the
 *   venue's account back in afterwards too (owner, 2026-10-19): Play puts
 *   Steam back on the PC's own account, but the customer's stays remembered
 *   in Steam's account list.
 * - `sign_in_launcher`: the same for any other launcher that has a route.
 *   Its own App was opened instead of the game, so the customer can sign the
 *   venue's account out and theirs in. Staff are told to sign the venue's
 *   account back in afterwards.
 *
 * A page built before this field existed ignores it and shows "Starting…".
 */
exports.PORTAL_LAUNCH_NEXT = ['play', 'sign_in_steam', 'sign_in_launcher'];
//# sourceMappingURL=portal.js.map